// Router lookup — when in internal mode, show DirectorDashboard.
// When in portal mode (client_id selected), show ClientPortalDashboard.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Briefcase, FolderOpen, Building, Calendar, ClipboardCheck, CheckSquare,
    AlertTriangle, FileText, Plus, Sparkles, ArrowUpRight, CheckCircle2, Clock,
    Upload, ShieldCheck, FileSignature, HardHat, Layers, ChevronDown,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import KpiCard from "@/components/KpiCard";
import DigestBanner from "@/components/DigestBanner";
import RenewalsPanel from "@/components/RenewalsPanel";
import CertExpiryWidget from "@/components/CertExpiryWidget";
import PhotoHealthWidget from "@/components/PhotoHealthWidget";
import Pill from "@/components/Pill";
import { pillForRisk, pillForStatus } from "@/lib/constants";
import { TID } from "@/lib/testIds";
import ClientPortalDashboard from "./ClientPortalDashboard";
import SiteManagerCockpit from "./SiteManagerCockpit";

const INTERNAL_ROLES = new Set(["director", "administrator", "consultant"]);

export default function DashboardRouter() {
    const { isInternalView, portalClientId } = useView();
    const { user } = useAuth();

    // Site managers get their own site-scoped cockpit (Phase 5).
    if (user && user.role === "site_manager") {
        return <SiteManagerCockpit />;
    }

    // Portal users (client_admin, reviewer) ALWAYS see their client portal —
    // they have no permission on /api/clients, so the director dashboard would 403.
    if (user && !INTERNAL_ROLES.has(user.role)) {
        const cid = portalClientId || user.client_id;
        if (!cid) {
            return <div className="font-serif-italic text-[#9A968F] py-12 text-center">
                No client assignment yet — please contact your Priority Risk contact.
            </div>;
        }
        return <ClientPortalDashboard clientId={cid} />;
    }
    if (!isInternalView && portalClientId) return <ClientPortalDashboard clientId={portalClientId} />;
    return <DirectorDashboard />;
}

function DirectorDashboard() {
    const [data, setData] = useState(null);
    const [clients, setClients] = useState([]);

    useEffect(() => {
        api.get("/dashboard/director").then((r) => setData(r.data));
        api.get("/clients").then((r) => setClients(r.data));
    }, []);

    if (!data) {
        return <div className="font-serif-italic text-[#9A968F] py-12 text-center">Loading dashboard…</div>;
    }

    const k = data.kpis;
    const mine = !!data.personal_actions;

    return (
        <div data-testid={TID.directorDashboard} className="space-y-7">
            {mine ? (
                <div className="space-y-2.5" data-testid="digest-banner-dual">
                    <DigestBanner personal />
                    <DigestBanner compact />
                </div>
            ) : (
                <DigestBanner />
            )}
            {/* Tag + section head */}
            <div>
                <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                      style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>
                    Phase 0 · Foundation
                </span>
                <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight text-[#1F1F1F]">
                            Operational overview
                        </h2>
                        <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                            {mine
                                ? `${k.active_buildings} buildings across the estate · ${k.outstanding_actions} action${k.outstanding_actions === 1 ? "" : "s"} assigned to you`
                                : `Six clients · ${k.active_buildings} buildings · ${k.outstanding_actions} open actions across the estate`}
                        </p>
                    </div>
                    <QuickActionsMenu />
                </div>
            </div>

            {/* KPI grid */}
            <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))" }}>
                <KpiCard kpiKey="active-clients"  icon={Briefcase}   label="Active clients"      value={k.active_clients}    tone="accent"
                         trend={k.onboarding_clients ? { label: `+${k.onboarding_clients} onboarding`, color: "#2A5DB0" } : null} />
                <KpiCard kpiKey="active-projects" icon={FolderOpen}  label="Active projects"     value={k.active_projects}   tone="neutral"
                         trend={k.completed_projects ? { label: `${k.completed_projects} closed`, color: "#3F7D3A" } : null} />
                <KpiCard kpiKey="active-buildings"icon={Building}    label="Active buildings"    value={k.active_buildings}  tone="neutral"
                         trend={k.high_risk_buildings ? { label: `${k.high_risk_buildings} high-risk`, color: "#B33A2E" } : null} />
                <KpiCard kpiKey="upcoming-visits" icon={Calendar}    label="Upcoming site visits" value={k.upcoming_visits}  tone="neutral" />
                <KpiCard kpiKey="open-audits"     icon={ClipboardCheck} label="Open audits"      value={k.open_audits}       tone="neutral" />
                <KpiCard kpiKey="outstanding-actions" icon={CheckSquare} label={mine ? "My open actions" : "Outstanding actions"} value={k.outstanding_actions} tone="amber"
                         trend={k.actions_due_week ? { label: `${k.actions_due_week} due this week`, color: "#C68A1C" } : null} />
                <KpiCard kpiKey="overdue-actions" icon={AlertTriangle} label={mine ? "My overdue actions" : "Overdue actions"}   value={k.overdue_actions}   tone="red" />
                <KpiCard kpiKey="docs-review"     icon={FileText}    label="Documents awaiting review" value={k.documents_review} tone="neutral" />
            </div>

            <ProjectPipeline data={data} />

            <CertExpiryWidget showClientName brand="var(--brand-accent, #C75A1B)" />

            <PhotoHealthWidget brand="var(--brand-accent, #C75A1B)" />

            {/* Two-up: client roster + recent activity */}
            <div className="grid lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 bg-white border rounded-[11px] shadow-brand-sm overflow-hidden"
                     style={{ borderColor: "#E5E0DA" }}>
                    <div className="px-5 py-3.5 flex items-center justify-between border-b" style={{ borderColor: "#F0EDE8" }}>
                        <div>
                            <h3 className="text-[14px] font-semibold text-[#1F1F1F]">Client roster</h3>
                            <p className="text-[11.5px]" style={{ color: "#6F6A64" }}>Status, risk and portal at a glance</p>
                        </div>
                        <a href="/app/clients" className="text-[11.5px] font-semibold flex items-center gap-1 text-[var(--brand-accent,_#C75A1B)] hover:underline">
                            View all <ArrowUpRight className="w-3 h-3" />
                        </a>
                    </div>
                    <table className="w-full text-[12.7px]">
                        <thead>
                            <tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                                <th className="text-left px-5 py-2.5 font-bold">Client</th>
                                <th className="text-left px-3 py-2.5 font-bold">Sector</th>
                                <th className="text-left px-3 py-2.5 font-bold">Buildings</th>
                                <th className="text-left px-3 py-2.5 font-bold">Portal</th>
                                <th className="text-left px-3 py-2.5 font-bold">Risk</th>
                                <th className="text-left px-3 py-2.5 font-bold">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clients.map((c) => (
                                <tr key={c.id} className="border-t hover:bg-[#FCFBF9] transition-colors" style={{ borderColor: "#F0EDE8" }}>
                                    <td className="px-5 py-3">
                                        <div className="flex items-center gap-2.5">
                                            <span className="w-7 h-7 rounded-[6px] grid place-items-center text-[10px] font-bold text-white"
                                                  style={{ background: c.branding.primary_colour }}>
                                                {c.branding.logo_initials}
                                            </span>
                                            <div>
                                                <div className="font-semibold text-[#1F1F1F]">{c.name}</div>
                                                <div className="text-[10.5px]" style={{ color: "#6F6A64" }}>{c.account_manager}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 py-3 text-[#2D2D2D]">{c.sector}</td>
                                    <td className="px-3 py-3 text-[#2D2D2D]">{c.buildings_count}</td>
                                    <td className="px-3 py-3">
                                        <Pill tone={c.portal_enabled ? "green" : "neutral"}>
                                            {c.portal_enabled ? "Enabled" : "Disabled"}
                                        </Pill>
                                    </td>
                                    <td className="px-3 py-3"><Pill tone={pillForRisk(c.risk)}>{c.risk}</Pill></td>
                                    <td className="px-3 py-3"><Pill tone={pillForStatus(c.status)}>{c.status}</Pill></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Recent activity + Beyond AI promo */}
                <div className="space-y-5">
                    <RenewalsPanel />
                    <div className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                        <div className="px-5 py-3.5 border-b" style={{ borderColor: "#F0EDE8" }}>
                            <h3 className="text-[14px] font-semibold text-[#1F1F1F]">Recent activity</h3>
                            <p className="text-[11.5px]" style={{ color: "#6F6A64" }}>From the audit log</p>
                        </div>
                        <ul className="divide-y" style={{ borderColor: "#F0EDE8" }}>
                            {data.recent_activity.length === 0 && (
                                <li className="px-5 py-6 text-center text-[12.5px] italic" style={{ color: "#9A968F" }}>
                                    No activity yet — events will appear here as the team uses the platform.
                                </li>
                            )}
                            {data.recent_activity.map((ev) => (
                                <li key={ev.id} className="px-5 py-3">
                                    <div className="text-[12.5px] text-[#1F1F1F]">
                                        <b>{ev.actor_email}</b> · <span className="text-[#6F6A64]">{ev.action}</span>
                                    </div>
                                    <div className="text-[10.5px] mt-0.5" style={{ color: "#9A968F" }}>
                                        {ev.entity_type} {ev.entity_id ? `· ${ev.entity_id.slice(0, 8)}` : ""}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="rounded-[11px] p-5 border bg-ai-grad-soft" style={{ borderColor: "#BFD8F5" }}>
                        <div className="flex items-center gap-2 mb-1">
                            <Sparkles className="w-4 h-4 text-[#1F6FE5]" />
                            <span className="text-[13px] font-bold text-[#1F6FE5]">Beyond AI</span>
                        </div>
                        <p className="text-[12px] leading-relaxed text-[#1F1F1F]">
                            The AI assistant, report writer and grounded knowledge base ship in <b>Phase 7</b>.
                            Phase 0 wires the <span className="font-mono">LLMService</span> interface in the backend
                            so later phases plug straight in.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

const PIPELINE_STAGES = [
    { key: "enquiry", label: "Enquiry" }, { key: "setup", label: "Setup" },
    { key: "pre_construction", label: "Pre-Con" }, { key: "site_visits", label: "Site Visits" },
    { key: "reporting", label: "Reporting" }, { key: "qa", label: "QA" },
    { key: "client_issue", label: "Client Issue" }, { key: "completion", label: "Completion" },
];

function ProjectPipeline({ data }) {
    const navigate = useNavigate();
    const byStage = data.projects_by_stage || {};
    const k = data.kpis;
    return (
        <div data-testid="project-pipeline" className="bg-white border rounded-[11px] shadow-brand-sm p-5" style={{ borderColor: "#E5E0DA" }}>
            <div className="flex items-center justify-between mb-3.5">
                <div>
                    <h3 className="text-[14px] font-semibold text-[#1F1F1F]">Project pipeline</h3>
                    <p className="text-[11.5px]" style={{ color: "#6F6A64" }}>Live count by workflow stage · click to drill in</p>
                </div>
                <a href="/app/projects" className="text-[11.5px] font-semibold flex items-center gap-1 text-[var(--brand-accent,_#C75A1B)] hover:underline">
                    All projects <ArrowUpRight className="w-3 h-3" />
                </a>
            </div>
            <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(96px,1fr))" }}>
                {PIPELINE_STAGES.map((s) => (
                    <button key={s.key} data-testid={`pipeline-stage-${s.key}`}
                        onClick={() => navigate(`/app/projects?stage=${s.key}`)}
                        className="rounded-[9px] border px-3 py-2.5 text-left hover:border-[var(--brand-accent,#C75A1B)] hover:bg-[#FBEFE5] transition-colors"
                        style={{ borderColor: "#E5E0DA" }}>
                        <div className="text-[20px] font-bold text-[#1F1F1F] leading-none">{byStage[s.key] || 0}</div>
                        <div className="text-[10.5px] font-semibold mt-1.5 uppercase tracking-wide" style={{ color: "#6F6A64" }}>{s.label}</div>
                    </button>
                ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-3.5 pt-3.5 border-t" style={{ borderColor: "#F0EDE8" }}>
                <PipeAlert label="Awaiting QA" value={k.projects_awaiting_qa} tone="#C68A1C" onClick={() => navigate("/app/projects?stage=qa")} testId="pipe-awaiting-qa" />
                <PipeAlert label="Awaiting client issue" value={k.projects_awaiting_client_issue} tone="#6B4FA0" onClick={() => navigate("/app/projects?stage=client_issue")} testId="pipe-awaiting-issue" />
                <PipeAlert label="Nearing completion" value={k.projects_nearing_completion} tone="#2A5DB0" onClick={() => navigate("/app/projects")} testId="pipe-nearing" />
                <PipeAlert label="Blocked" value={k.projects_blocked} tone="#B33A2E" onClick={() => navigate("/app/projects?flag=Blocked")} testId="pipe-blocked" />
            </div>
        </div>
    );
}

function PipeAlert({ label, value, tone, onClick, testId }) {
    return (
        <button onClick={onClick} data-testid={testId}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11.5px] font-semibold hover:bg-[#F6F4F0]"
            style={{ borderColor: "#E5E0DA", color: "#2D2D2D" }}>
            <span className="w-5 h-5 rounded-full grid place-items-center text-white text-[10px] font-bold" style={{ background: tone }}>{value || 0}</span>
            {label}
        </button>
    );
}


const QUICK_ACTION_GROUPS = [
    {
        group: "Clients & Sites",
        items: [
            { label: "Create client", testid: "create-client", icon: Briefcase, to: "/app/clients?new=1" },
            { label: "Create site / building", testid: "create-building", icon: Building, to: "/app/buildings?new=1" },
        ],
    },
    {
        group: "Projects & Delivery",
        items: [
            { label: "Create project", testid: "create-project", icon: FolderOpen, to: "/app/projects?new=1" },
            { label: "Schedule site visit", testid: "schedule-visit", icon: Calendar, to: "/app/schedule?new=1" },
            { label: "Start audit", testid: "start-audit", icon: ClipboardCheck, to: "/app/inspections?new=1" },
            { label: "Create action", testid: "create-action", icon: CheckSquare, to: "/app/actions?new=1" },
        ],
    },
    {
        group: "Safety & Compliance",
        items: [
            { label: "Log accident / incident", testid: "log-incident", icon: AlertTriangle, to: "/app/incidents?new=1" },
            { label: "Create Building Safety Case record", testid: "create-bsc", icon: ShieldCheck, to: "/app/bsc?new=1" },
        ],
    },
    {
        group: "Documents & Reports",
        items: [
            { label: "Upload document", testid: "upload-document", icon: Upload, to: "/app/documents?new=1" },
            { label: "Generate report", testid: "generate-report", icon: FileText, to: "/app/reports" },
        ],
    },
    {
        group: "RAMS / CPP / PCI",
        items: [
            { label: "Create RAMS", testid: "create-rams", icon: FileSignature, to: "/app/rams" },
            { label: "Create CPP", testid: "create-cpp", icon: HardHat, to: "/app/cpp?new=cpp" },
            { label: "Create PCI / PCIP pack", testid: "create-pci", icon: Layers, to: "/app/cpp?new=pci" },
        ],
    },
];

function QuickActionsMenu() {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const ref = useRef(null);

    useEffect(() => {
        function onDocClick(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        if (open) document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    function handle(item) {
        setOpen(false);
        if (item.stub) {
            toast.info(`${item.label} — this builder ships in an upcoming phase.`);
            return;
        }
        navigate(item.to);
    }

    return (
        <div className="relative" ref={ref}>
            <button data-testid="dashboard-quick-actions-trigger"
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white"
                style={{ background: "#1F1F1F" }}>
                <Plus className="w-3.5 h-3.5" /> Quick action
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div data-testid="dashboard-quick-actions-menu"
                    className="absolute right-0 mt-2 w-[270px] bg-white border rounded-[11px] shadow-brand-lg py-2 z-40 max-h-[72vh] overflow-y-auto"
                    style={{ borderColor: "#E5E0DA" }}>
                    {QUICK_ACTION_GROUPS.map((g) => (
                        <div key={g.group} className="px-1">
                            <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider" style={{ color: "#9A968F" }}>
                                {g.group}
                            </div>
                            {g.items.map((item) => (
                                <button key={item.label} data-testid={`quick-action-${item.testid}`}
                                    onClick={() => handle(item)}
                                    className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-[7px] text-[12.5px] text-[#1F1F1F] hover:bg-[#F6F4F0] transition-colors">
                                    <item.icon className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--brand-accent, #C75A1B)" }} />
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
